import { useEffect, useRef } from 'react';

/**
 * Mouse-reactive aurora background — additive canvas blobs that drift on their
 * own and gently lean toward the pointer. No libraries; respects reduced motion.
 */
export default function Aurora({ className = '', intensity = 1, interactive = true }) {
  const canvasRef = useRef(null);
  const pointer = useRef({ x: 0.5, y: 0.4, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const blobs = [
      { x: 0.22, y: 0.28, r: 0.55, c: [109, 94, 252], px: 0, py: 0, s: 0.6 },
      { x: 0.8, y: 0.22, r: 0.48, c: [52, 231, 228], px: 0, py: 0, s: 0.5 },
      { x: 0.62, y: 0.82, r: 0.6, c: [232, 91, 255], px: 0, py: 0, s: 0.7 },
      { x: 0.32, y: 0.72, r: 0.45, c: [130, 123, 255], px: 0, py: 0, s: 0.45 },
    ];

    let w, h, dpr, raf;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    let t = 0;
    const render = () => {
      t += 0.005;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      const pull = pointer.current;
      for (let i = 0; i < blobs.length; i++) {
        const b = blobs[i];
        const driftX = reduce ? 0 : Math.sin(t * b.s + i) * 0.06;
        const driftY = reduce ? 0 : Math.cos(t * b.s * 0.8 + i * 2) * 0.06;
        // ease toward pointer
        const targetX = interactive && pull.active ? (pull.x - b.x) * 0.12 : 0;
        const targetY = interactive && pull.active ? (pull.y - b.y) * 0.12 : 0;
        b.px += (targetX - b.px) * 0.04;
        b.py += (targetY - b.py) * 0.04;

        const cx = (b.x + driftX + b.px) * w;
        const cy = (b.y + driftY + b.py) * h;
        const rad = b.r * Math.max(w, h);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
        const [r, gr, bl] = b.c;
        g.addColorStop(0, `rgba(${r}, ${gr}, ${bl}, ${0.16 * intensity})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, rad, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(render);
    };
    render();

    let onMove, onLeave;
    if (interactive) {
      onMove = (e) => {
        const rect = canvas.getBoundingClientRect();
        pointer.current = {
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
          active: true,
        };
      };
      onLeave = () => (pointer.current.active = false);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerleave', onLeave);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      if (onMove) window.removeEventListener('pointermove', onMove);
      if (onLeave) window.removeEventListener('pointerleave', onLeave);
    };
  }, [intensity, interactive]);

  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden>
      <canvas ref={canvasRef} className="h-full w-full" />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(120% 120% at 50% 0%, transparent 45%, rgb(13 18 30 / 0.94) 100%)' }}
      />
    </div>
  );
}
